/**
 * 报表页**中文文案唯一源** + **数值 → 文本**（K 域 · 端侧）
 *
 * 依据：K 域 PRD **§4.1**（四段）· **§4.5.4**（三层结论句 + 一个动作）· `AC-K1.5`（除零显示「—」）·
 * `AC-K4.13`（数据不足）· `AC-K4.15`（动作句由 kind + 数字拼）· `AC-K4.16`（泛泛建议不显示）·
 * `AC-K5.10`（P1 占位文案）· `DR-036`（金额文本走共享层单一源）。
 *
 * ## 🔴 端侧零口径（本文件一个数都不算）
 *
 * 数字**全部来自服务端**；本文件只做三件事：① 选词；② 排版数字（`percentText` 复用预算页的、
 * 金额走 `formatMoneyText`）；③ 按 `kind` 把服务端给的数字**拼成句子**。
 *
 * 🔴 **不在这里重算任何指标** —— 「应急金 2.3 个月」里的 `2.3` 来自 `metric.valueE4`，
 * 「每月多存 ¥1,500」里的 `1500` 来自 `advice.amount`（`nucleus/health-advice.ts` 算的）。
 */

import { ReportPolicy, formatMoneyText } from '@jiaying/shared';
import type {
  CategoryNodeView,
  HealthAdviceView,
  HealthLayerKey,
  HealthMetricView,
  ReportWindowView,
  TrendBucketView,
} from '@jiaying/shared';
import { divideExact } from '@jiaying/shared';
// 🔴 百分比文本**复用预算页的同一函数**（不写第二份 —— 它内部已处理"四舍五入拿整数位"的坑）
import { percentText } from './budget-labels.js';

// ─────────────────────────── 段落与层的中文名 ───────────────────────────

/** 四段标题（原型 `13-报表_原型.html` 的四个 `data-anchor` 文案） */
export const SEGMENT_TITLES = {
  spent: '已发生 · 钱花到哪儿去了',
  now: '现在 · 家底什么样',
  soon: '近期 · 这个月撑得住吗',
  future: '远期 · 这样下去会怎样',
} as const;

/**
 * 🆕 **报表时间段档位**（批 `109` · 负责人 2026-10-26：「报表中『钱花去哪里了』可以提供不同时间段的选择」）。
 *
 * 🔴 这三档是**交互档位**，不是口径：真正的窗口由服务端按 `months` 解析
 * （`resolveWindow()` → `recentMonthsStart(today, months)`，见 `report.service.ts`），
 * 端侧只把用户点的档位原样传上去，**不自己算起止日**（铁律 17 / 账本时区）。
 *
 * ⚠️ 上限由服务端 `ReportPolicy.MaxTrendMonths`（36）兜底；端侧不重复声明这个上限。
 */
export const TREND_MONTH_OPTIONS = [3, 6, 12] as const;

/**
 * 默认档 —— 🔴 从共享层 `ReportPolicy.DefaultTrendMonths` 取，**不要在这里写 12**：
 * 服务端不传 `months` 时用的就是那个值，两处写死就会在它改值时"端侧以为在看 12 个月"。
 */
export const REPORT_DEFAULT_MONTHS: number = ReportPolicy.DefaultTrendMonths;

/** 档位 → 按钮文案 */
export function trendMonthLabel(months: number): string {
  return `近 ${String(months)} 个月`;
}

/**
 * 当前窗口的**一行说明**（`from ~ to · N 个月`）。
 *
 * 🔴 必须显示：不同档位下「日均 / 同环比」的分母都变了，用户不看见窗口就会拿两个月度指标互相比较。
 * 数据全部来自服务端 `report.window`，端侧不补算。
 */
export function reportWindowText(w: ReportWindowView): string {
  return `${w.from} ~ ${w.to} · ${String(w.months)} 个月${w.custom ? '（自定义区间）' : ''}`;
}

/** 三层的**问题句**（`AC-K4.10`：三层组织的可读性来源） */
export const LAYER_QUESTIONS: Record<HealthLayerKey, string> = {
  risk: '扛不扛得住',
  efficiency: '钱留不留得住',
  structure: '钱摆得对不对',
};

/** 未分类哨兵的中文名 —— 🔴 **只能在这里**（服务端刻意不下发，见 `report.service.ts`） */
export const UNCATEGORIZED_LABEL = '未分类';

/** 「数据不足」（分母为 0）与「未就绪」（来源没落地）**必须长得不一样**（`AC-K4.13`） */
export const INSUFFICIENT_TEXT = '数据不足';
export const UNAVAILABLE_PREFIX = '未就绪：';

export const EMPTY_SPENT_HINT = '这个窗口里还没有计入收支的支出。';
/** 🔴 下钻空态必须解释（`AC-K2.10`）—— 本批下钻未接，但文案先就位（见 changelog 的诚实登记） */
export const AMORTIZED_ONLY_HINT = '该行金额来源为摊销，无对应流水明细。';

// ───────────────────── ① 段下钻抽屉（`AC-K2.9` / `AC-K2.10`） ─────────────────────

/** 抽屉标题：`下钻 · 教育（仅流水口径）`（原型 `#drCat` 的 `.card-head > h3`） */
export function drillTitle(name: string): string {
  return `下钻 · ${name}（仅流水口径）`;
}

/** 口径徽标（原型 `.caliber`） */
export const DRILL_CALIBER = 'statsOnly = true';

/** 🔴 空态第二句（原型 `#drEmpty` 原文）：解释"为什么没有明细"，**不显示空白误导** */
export const DRILL_EMPTY_EXPLAIN =
  '不是"没有数据"，而是这笔钱本期没有"记一笔"的动作 —— 明确说明，不显示空白误导。';

/** 抽屉载入 / 出错 */
export const DRILL_LOADING = '正在取该分类的流水明细…';
export const DRILL_ERROR = '明细加载失败，请稍后重试。';
/** 条数（🔴 **不求和** —— 抽屉是"仅流水口径"，求和在端侧就是第二套金额口径） */
export function drillCountText(n: number): string {
  return `共 ${String(n)} 笔（仅流水口径）`;
}

/**
 * 🔴 **两套口径的差异标注**（`AC-K2.9`：UI 必须标注，不得让用户以为两者应相等）。
 *
 * 原型 `#drCat` 的 `.t-11` 原文：「抽屉合计 ¥12,278 ≠ 卡片 ¥12,010 —— 差额来自**待摊摊销**
 * （卡片含、抽屉不含）。**这是设计如此。**」
 * ⚠️ 本实现**只给出卡片一侧的金额**（我们有它），抽屉侧的合计**刻意不算** ——
 * 端侧对"仅流水"求和会引入第二套金额口径（对账口径只在共享层）。
 */
export function drillCaliberNote(cardAmountText: string): string {
  return `卡片金额 ${cardAmountText}（含待摊摊销）· 本抽屉仅列流水明细（不含摊销）⇒ 两者可以不等，这是设计如此。`;
}

/** ④ 段 P1 占位文案（`AC-K5.10`：不留空白区，且说明"为什么现在是空的"） */
export const P1_TITLE = '远期推演（P1）';
export const P1_NOTE =
  '这一版先留位置：等「储蓄目标」与「贷款计划表」落地后，这里由你**自己设定条件**（每期攒多少 / 每月提前还款多少），系统只按你给的条件做算术 —— 我们不会替你假设未来。';

/** 缺汇率提示（与该条纪律的其它落点同文） */
export const FX_INCOMPLETE_NOTE = '有支出因缺少当日汇率未被折算，本币合计不完整。';

// ─────────────────────────── 数值 → 文本 ───────────────────────────

/**
 * 应急月数：`valueE4` 是**「月 × E4」**（`23000` = 2.30 个月），不是百分比。
 *
 * 🔴 **不能用 `percentText`**：那会显示 `230.00%`（v1 的"两套均值同名"同型错误）。
 * 整数位用**整除**（先减去余数）—— `divideExact` 是四舍五入，直接拿它当整数位会把
 * `13.50` 舍成 `14.00`。
 */
export function monthsText(e4: number): string {
  const whole = divideExact(e4 - (e4 % 10_000), 10_000);
  const rest = e4 % 10_000;
  const hundredths = divideExact(rest - (rest % 100), 100);
  return `${String(whole)}.${String(hundredths).padStart(2, '0')}`;
}

/** 指标 → 显示文本（`null` 一律给「—」，**不给 0**） */
export function metricText(m: HealthMetricView): string {
  if (m.valueE4 === null) return '—';
  return m.key === 'emergencyMonths' ? `${monthsText(m.valueE4)} 个月` : percentText(m.valueE4);
}

/** 同环比（`null` ⇒ 「—」，`AC-K1.5`：**不显示 ∞、不伪装成 0%**） */
export function changeText(e4: number | null): string {
  return e4 === null ? '—' : percentText(e4);
}

// ─────────────────────────── 动作句（kind + 数字） ───────────────────────────

/**
 * 把服务端的 `kind` + 数字拼成**一句话**。
 *
 * 🔴 三个 `kind` 各有一套句式；`amount` / `months` / `targetE4` 的**语义随 kind 变**
 * （见 `contract/report.ts` 的 `HealthAdviceView` 注释），故**必须一起消费**。
 * 🔴 没有数字支撑的动作**根本不会到这里**（服务端给 `null` ⇒ 调用方不渲染动作行）。
 */
export function adviceText(advice: HealthAdviceView, currency: string): string {
  const target = advice.targetE4 === null ? '' : percentText(advice.targetE4);
  switch (advice.kind) {
    case 'emergency-topup': {
      const amount = advice.amount === null ? '' : formatMoneyText(advice.amount, currency);
      const months = advice.months === null ? '' : String(advice.months);
      const targetMonths = advice.targetE4 === null ? '' : monthsText(advice.targetE4);
      return `每月多存 ${amount}，约 ${months} 个月后应急金可到 ${targetMonths} 个月线`;
    }
    case 'cut-expense':
      return `把支出压 ${percentText(ReportPolicy.ExpenseCutRatioE4)}，储蓄率可到 ${target} 附近`;
    case 'trim-premium': {
      const amount = advice.amount === null ? '' : formatMoneyText(advice.amount, currency);
      return `年度保费压到收入的 ${target}，每年可省下 ${amount}`;
    }
    default: {
      // 穷尽检查（新增 kind 时此处会变成类型错误）
      const never: never = advice.kind;
      return String(never);
    }
  }
}

// ─────────────────────────── 分类行 / 趋势柱 ───────────────────────────

/** 分类占比树 → **扁平行**（渲染用；深度用 `level` 表达，端侧不重排顺序） */
export interface CategoryRow {
  id: string;
  name: string;
  level: 1 | 2;
  amountCents: number;
  shareText: string;
  /** 🆕 批 `112-B`：该分类已被用户移除，但历史支出仍归属它（`AC-B3.13` 报表侧） */
  removed: boolean;
}

/**
 * 分类名（报表行用）。🆕 已移除的分类加后缀 ——
 * 否则用户会以为"我明明移除了，报表里怎么还在"，而真相是**历史支出仍归它**。
 */
function reportCategoryName(n: CategoryNodeView): string {
  if (n.isUncategorized) return UNCATEGORIZED_LABEL;
  return n.removed === true ? `${n.name} · 已移除` : n.name;
}

export function categoryRows(nodes: readonly CategoryNodeView[]): CategoryRow[] {
  const rows: CategoryRow[] = [];
  for (const n of nodes) {
    rows.push({
      id: n.categoryId,
      name: reportCategoryName(n),
      level: 1,
      amountCents: n.amount,
      shareText: percentText(n.shareE4),
      removed: n.removed === true,
    });
    for (const c of n.children) {
      rows.push({
        id: c.categoryId,
        name: reportCategoryName(c),
        level: 2,
        amountCents: c.amount,
        shareText: percentText(c.shareE4),
        removed: c.removed === true,
      });
    }
  }
  return rows;
}

/** 趋势柱（末端是当月；`paid` 仅供宽度百分比用，**不是金额口径**） */
export interface TrendBar {
  month: string;
  label: string;
  expenseCents: number;
  /** 0–100：相对本窗口最大值（🔴 唯一的端侧算术，且不参与任何金额显示） */
  percent: number;
}

export function trendBars(trend: readonly TrendBucketView[]): TrendBar[] {
  let max = 0;
  for (const b of trend) if ((b.expense as number) > max) max = b.expense as number;
  return trend.map((b) => ({
    month: b.month,
    label: b.month.slice(5),
    expenseCents: b.expense,
    // 🔴 先脱离 brand 再算（`no-float-mul-on-brand`）—— 条宽**不是金额**，不参与任何金额显示
    percent: max === 0 ? 0 : divideExact(((b.expense as number) * 100), max),
  }));
}
